import { useState, useEffect, useCallback } from 'react'
import { api } from '@api/client'
import { appsyncQuery } from '@api/appsync-client'

const GET_ADMIN_SETTINGS = `
  query GetAdminSettings {
    getAdminSettings {
      defaultLayout sessionRetention allowedDomains suspensionRetentionDays
      dataRetentionDays deletionGraceDays mfaEnforced
      notifications { orphanAlert weeklyDigest }
    }
  }
`

const UPDATE_ADMIN_SETTINGS = `
  mutation UpdateAdminSettings($input: UpdateAdminSettingsInput!) {
    updateAdminSettings(input: $input)
  }
`

const LIST_ADMINS = `
  query ListAdmins {
    listAdmins {
      userId email name grantedAt grantedBy
    }
  }
`

export function useAdminSettings() {
  const [settings, setSettings] = useState(null)
  const [admins, setAdmins] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [s, a] = await Promise.all([
        appsyncQuery(GET_ADMIN_SETTINGS).then((d) => d.getAdminSettings),
        appsyncQuery(LIST_ADMINS).then((d) => d.listAdmins),
      ])
      setSettings(s)
      setAdmins(a || [])
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const updateSettings = useCallback(async (body) => {
    await appsyncQuery(UPDATE_ADMIN_SETTINGS, { input: body })
    load()
  }, [load])

  const addAdmin = useCallback(async (email) => {
    await api.adminAddAdmin(email)
    load()
  }, [load])

  const removeAdmin = useCallback(async (userId) => {
    await api.adminRemoveAdmin(userId)
    load()
  }, [load])

  return { settings, admins, loading, error, updateSettings, addAdmin, removeAdmin }
}
